import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { extractApiError } from '../../../../core/utils/api-format';
import { QuotaService } from '../../../quotas/services/quota.service';
import { ConclusionVolume } from '../../../quotas/models/conclusion-volume.interface';

export interface UpdateDeliveredVolumeDialogData {
  volume: ConclusionVolume;
}

@Component({
  selector: 'app-update-delivered-volume-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TranslateModule
  ],
  templateUrl: './update-delivered-volume-dialog.component.html',
  styleUrls: ['./update-delivered-volume-dialog.component.css']
})
export class UpdateDeliveredVolumeDialogComponent {
  private quotaService = inject(QuotaService);
  private dialogRef = inject(MatDialogRef<UpdateDeliveredVolumeDialogComponent>);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  data = inject<UpdateDeliveredVolumeDialogData>(MAT_DIALOG_DATA);

  loading = signal(false);
  errorMessage = signal('');
  fieldErrors = signal<Record<string, string>>({});

  organizationInn = '';
  personIdnp = '';
  deliveredVolume: number | null = null;

  onKeyDown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (!allowed.includes(event.key) && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onNumericInput(event: Event, field: 'personIdnp' | 'organizationInn'): void {
    const input = event.target as HTMLInputElement;
    const filtered = input.value.replace(/\D/g, '').slice(0, 14);
    input.value = filtered;
    this[field] = filtered;
    const errors = { ...this.fieldErrors() };
    delete errors[field];
    this.fieldErrors.set(errors);
  }

  isFormValid(): boolean {
    return !!(
      this.organizationInn.length === 14 &&
      this.personIdnp.length === 14 &&
      this.deliveredVolume &&
      this.deliveredVolume > 0
    );
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.errorMessage.set('');
    this.fieldErrors.set({});
    this.loading.set(true);

    const deliveredVolumeInKg = this.deliveredVolume! * 1000;

    this.quotaService.updateDeliveredVolume({
      id: this.data.volume.id,
      deliveredVolume: deliveredVolumeInKg,
      personIdnp: this.personIdnp,
      organizationInn: this.organizationInn
    }).subscribe({
      next: (response: any) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open(this.translate.instant('updateDeliveredVolumeDialog.success'), 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.dialogRef.close({ success: true });
        } else {
          this.handleApiErrors(response);
        }
      },
      error: (error: any) => {
        console.error('Error updating delivered volume:', error);
        this.handleApiErrors(error.error || {});
        this.loading.set(false);
      }
    });
  }

  private handleApiErrors(response: { msg?: string; res?: any }): void {
    const items: Array<{ field?: string | null; ru: string }> = Array.isArray(response.res) ? response.res : [];
    const errors: Record<string, string> = {};
    items.filter(err => err.field).forEach(err => errors[err.field as string] = err.ru);
    if (Object.keys(errors).length) {
      this.fieldErrors.set(errors);
      return;
    }
    this.errorMessage.set(extractApiError(response, this.translate.instant('updateDeliveredVolumeDialog.error'), this.translate.currentLang));
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
